import { useEffect } from 'react';
import { GetServerSideProps } from 'next';
import { CollectionPageTemplate } from '@/components/templates';
import Layout from '@/components/layout';
import { getArtisanList } from '@/lib/api.artisan';
import {
  getCollectionTags,
  getEntries,
  // getCurrencies,
  getCollectionPagePatterns,
} from '@/lib/api.cms';
import { getLocales } from '@/lib/api.locale';
import {
  overrideMetadata,
  groupCollectionTags,
  compareStrings,
} from '@/utils/index';
import {
  getCmsSlug,
  getSelectedGender,
  getSelectedHandle,
  getSelectedCategory,
  getSelectedSubCategory,
  replaceHeadingPlaceholders,
  createCatalogQueries,
  getCategoriesAndFilters,
} from '@/utils/Catalog';
import { filterValidQueryParams } from '@/utils/Filters';
import { createLabelFromSlug } from '@/utils/index';
import {
  CollectionPageContent,
  IApiFiltersFacetValue,
  ICollectionPlaceholdersHeading,
} from '@/interfaces/index';
import { META_ROBOTS_DEFAULT } from '@/lib/constants';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { ICollectionPage, IData, IPage } from '@/types/index';
import {
  ICollectionTagFields,
  ICollectionPage as ICollectionPageContentful,
} from '@/types/contentful/contentful';
import { getSettings } from '@/utils/Settings';
import { getSettingsTheme } from '@/utils/SettingsTheme';
import logger from '@/lib/logger';
import { safeFetchAll } from '@/utils/typings/app-fetch';
import { mapResult, Result } from '@/utils/typings/result';
import { mapSearchResult } from '@/lib/models/mappers/product';
import { SearchResultResponse } from '@/types/catalog';
import { ProductSearch } from '@/lib/models/types/product';
import { AppError } from '@/utils/typings/errors';
import { IMirtaServerProps } from '@/types/IMirtaServerProps';

import { getCustomerInfoServerSide } from '@/utils/Account';
const CollectionPage: React.FC<IPage<ICollectionPage>> = ({
  locale,
  locales,
  preview,
  currency,
  data,
  pageFields,
  collectionTags,
  collectionPagePatterns,
  catalogProducts,
  catalogFilters,
  catalogCategories,
  serverSideCatalogQuery,
}) => {
  useEffect(() => {
    window.history.scrollRestoration = 'manual';
  }, []);

  return (
    <Layout
      layout={data.layout}
      preview={preview}
      locales={locales}
      locale={locale}
      currency={currency}>
      {catalogProducts && (
        <CollectionPageTemplate
          content={data.content}
          cmsPage={pageFields}
          cmsCollectionTags={collectionTags}
          catalogFilters={catalogFilters}
          catalogProducts={catalogProducts}
          locale={locale}
          catalogCategories={catalogCategories}
          collectionPagePatterns={collectionPagePatterns}
          serverSideCatalogQuery={serverSideCatalogQuery}
        />
      )}
    </Layout>
  );
};

export default CollectionPage;

export const getServerSideProps: GetServerSideProps<
  IMirtaServerProps<ICollectionPage>
> = async context => {
  const { locale = 'en', query, preview = false, req, resolvedUrl } = context;
  const { currency, segmentPrice } = await getCustomerInfoServerSide(req);
  const locales = getLocales();
  let { slug, currency: currencyQueryParam, ...queryParams } = query;
  const validQueryParams = filterValidQueryParams(queryParams) || null;

  let slugValues: string[] = [];
  let handle = '';

  // CMS query to retrieve all COLLECTION_TAGS
  let collectiontagsPromise = getCollectionTags(locale, preview);
  // CATALOG query to retrieve all ARTISANS
  let artisansPromise = getArtisanList();
  let artisans: IApiFiltersFacetValue[] = [];
  let collectionTagArray: ICollectionTagFields[] = [];
  let results;
  try {
    results = await Promise.all([collectiontagsPromise, artisansPromise]);
    collectionTagArray = results?.[0];
    artisans = results?.[1].facets?.[0].values;
  } catch (err) {
    logger.err(err);
  }

  const collectionTags = groupCollectionTags(collectionTagArray);

  // it maps Artisans retrieved from CATALOG in the same structure as the COLLECTION_TAGS retrieved from CMS ....
  let normalizedArtisans: ICollectionTagFields[] = [];
  if (artisans && artisans.length > 0) {
    normalizedArtisans = artisans.map(artisan => {
      return {
        slug: artisan.query_value,
        label: artisan.display_value,
        tag: '',
      };
    });
  }
  // and it puts them in the "filters" group
  collectionTags.filters.artisan = normalizedArtisans;

  // for ease of use it extrapolates GENDERS from collection_tags
  let genderObjects = collectionTagArray.filter(collFilter => {
    if (collFilter?.tag) {
      return collFilter.tag.startsWith('GENDER_');
    }
  });
  genderObjects = genderObjects.sort((obj1, obj2) => {
    return compareStrings(obj1.label || obj1.slug, obj2.label || obj2.slug);
  });

  let selectedCategories: string[] = [];
  if (slug) {
    if (!Array.isArray(slug)) {
      slug = [slug];
    }
    if (slug.length >= 1) {
      handle = slug[0];
      if (slug.length >= 2) {
        selectedCategories = slug.slice(1);
      }
    }
  }

  let selectedGender = getSelectedGender(genderObjects, handle);

  const cmsSlug = getCmsSlug(slug as string[], selectedGender);
  // cmsSlug is needed to retrieve the right "collectionPage" from CMS

  if (slug && slug.length > 0) {
    slugValues = [
      selectedGender ? (slug as string[]).join('/') : slug[0],
      cmsSlug,
    ];
  } else {
    slugValues = ['all'];
  }

  // CMS query to retrieve "collectionPage" structure
  const page = (await getEntries(
    'collectionPage',
    'slug',
    slugValues,
    locale,
    preview
  )) as ICollectionPageContentful;

  if (!page || !page.fields) {
    return {
      notFound: true,
    };
  }

  let selectedCollection: ICollectionTagFields | undefined | null,
    selectedCategory: ICollectionTagFields | undefined | null,
    selectedSubCategory: ICollectionTagFields | undefined | null;

  let metaPlaceholders: { [key: string]: string } = {};

  if (handle) {
    selectedGender = genderObjects.find(genderObj => {
      return genderObj.slug === handle;
    });
  }

  if (!selectedGender) {
    selectedCollection = getSelectedHandle(collectionTags, handle);
  }

  if (selectedCategories && selectedCategories[0]) {
    selectedCategory = getSelectedCategory(
      collectionTags,
      selectedCategories[0]
    );
    if (selectedCategories[1]) {
      selectedSubCategory = getSelectedSubCategory(
        collectionTags,
        selectedCategories[0],
        selectedCategories[1]
      );
      if (selectedCategories[2]) {
        selectedSubCategory = getSelectedSubCategory(
          collectionTags,
          selectedCategories[0],
          selectedCategories[2]
        );
      }
    }
  }

  // fetch collectionPagePatterns  from CMS
  const collectionPagePatterns = await getCollectionPagePatterns(
    locale,
    preview
  );

  let placeholdersHeading: ICollectionPlaceholdersHeading = {
    placeholdersTitle: page.fields.title || null,
    placeholdersDescription: page.fields.descriptionRich || null,
    placeholdersDescriptionBottom: page.fields.bottomDescriptionRich || null,
  };

  const heading = replaceHeadingPlaceholders(
    placeholdersHeading,
    slug as string[],
    genderObjects,
    collectionTags
  );

  const pageFields: CollectionPageContent = {
    ...page.fields,
    ...heading,
  };

  let canonicalPage = '';
  if (parseInt(validQueryParams.p as string, 10) > 1) {
    canonicalPage = `?p=${validQueryParams.p}`;
  }

  const canonicalUrl = {
    canonical: `${process.env.BASE_DOMAIN_URL}${
      locale !== context.defaultLocale ? '/' + locale : ''
    }${context.resolvedUrl.split('?')[0]}${canonicalPage}`,
  };

  const robots = {
    robots:
      page.fields.meta &&
      page.fields.meta.fields &&
      page.fields.meta.fields.robots
        ? page.fields.meta.fields.robots
        : META_ROBOTS_DEFAULT,
  };

  const data: IData = {
    layout: {
      header: page.fields.layout?.fields?.header?.fields || null,
      footer: page.fields.layout?.fields?.footer?.fields || null,
      meta: { ...page.fields.meta?.fields, ...canonicalUrl, ...robots },
    },
    content: [...(page.fields.layout?.fields?.footer?.fields?.section || [])],
  };

  const settings = getSettings(data);

  if (handle) {
    if (selectedGender) {
      metaPlaceholders['gender'] =
        selectedGender?.label || createLabelFromSlug(handle);
    } else {
      metaPlaceholders['handle'] =
        selectedCollection?.label || createLabelFromSlug(handle);
    }
  }

  if (selectedCategories && selectedCategories.length > 0) {
    if (selectedCategories[0]) {
      metaPlaceholders['category'] =
        selectedCategory?.label || createLabelFromSlug(selectedCategories[0]);
    }

    if (selectedCategories[2]) {
      metaPlaceholders['subcategory2'] =
        selectedSubCategory?.label ||
        createLabelFromSlug(selectedCategories[2]);
    } else {
      if (selectedCategories[1]) {
        metaPlaceholders['subcategory'] =
          selectedSubCategory?.label ||
          createLabelFromSlug(selectedCategories[1]);
      }
    }
  }

  if (Object.keys(metaPlaceholders).length > 0) {
    overrideMetadata(data.layout.meta, metaPlaceholders);
  }

  // CATALOG queries

  const catalogQueries: {
    [k: string]: string;
  } = createCatalogQueries(
    query,
    resolvedUrl,
    collectionTags,
    currency,
    settings!,
    segmentPrice ?? undefined
  );

  let catalogData: Result<
    { url: string; result: ProductSearch }[],
    AppError
  > | null = null;

  const urls = Object.keys(catalogQueries);
  const catalogResults = await safeFetchAll<SearchResultResponse>(
    Object.keys(catalogQueries)
  );

  catalogData = mapResult(catalogResults, result =>
    result.map((result, index) => ({
      url: urls[index],
      result: mapSearchResult(result),
    }))
  );

  if (catalogData._tag === 'error') {
    const errMsg = 'No data to show : ' + catalogData.error.message;
    logger.error(errMsg);
    return {
      notFound: true,
    };
  }

  let filters, categories, mainResult;
  if (catalogData) {
    const toShow = getCategoriesAndFilters(
      slug as string[],
      catalogQueries,
      catalogData.result,
      collectionTags,
      currency as string
    );
    //TODO: remove once all products in catalog have season filled
    if (toShow.filters?.season) {
      delete toShow.filters.season;
    }
    filters = toShow.filters;
    categories = toShow.categories;
    mainResult = toShow.mainUrlResult;
  } else {
    logger.error('No data to show');
    throw new Error('No data to show');
  }

  const settingsTheme = getSettingsTheme();

  return {
    props: {
      pageType: 'collection',
      locale,
      locales: locales.items,
      preview: preview || false,
      currency,
      data,
      pageFields,
      settings,
      collectionTags,
      settingsTheme,
      catalogProducts: mainResult
        ? {
            facets: [],
            items: mainResult.items,
            pagination: mainResult.pagination,
            metadata: {},
          }
        : null,
      catalogFilters: filters || null,
      catalogCategories: categories || null,
      collectionPagePatterns,
      serverSideCatalogQuery: {
        currency: currency as string,
        path: resolvedUrl,
      },
      ...(await serverSideTranslations(locale, [
        'common',
        'account',
        'product',
        'catalog',
      ])),
    },
  };
};
